// ── AuthContext ─────────────────────────────────────────────
// Sanctum-shaped contract (Plan 01-01, D-05):
//   • bootCsrf() once on mount — primes the XSRF-TOKEN cookie.
//   • Magic-link flow: login/register both POST /api/auth/magic-link/request
//     (Plan 03 ships the controller). The actual session is established by the
//     server-side consume redirect; the SPA detects it via GET /api/me on
//     /app load.
//   • Context exports (useAuth, AuthProvider) preserved so ProtectedRoute and
//     existing pages keep compiling. Plan 03 rewires the login/register pages.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import i18n from 'i18next';
import { api, bootCsrf, setOnAuthError } from '@/lib/api-client';
import type { User, LoginCredentials, RegisterData } from '@/types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isInitialized: false,
  });

  const clearAuth = useCallback(() => {
    setState({ user: null, isLoading: false, isInitialized: true });
  }, []);

  // Set up auth error handler (401 → auto-logout)
  useEffect(() => {
    setOnAuthError(clearAuth);
  }, [clearAuth]);

  /**
   * The SPA follows `users.locale` once there is a user (2026-08-19).
   *
   * Two halves of this app were picking a language independently. This one used
   * `i18next-browser-languagedetector` and nothing else; the server reads the
   * `users.locale` column for every `__()` call, every mailable and every
   * consent text. Nothing reconciled them.
   *
   * The operator hit it: his account is stored `en` while his browser asked for
   * German, so he read the ENGLISH photo-consent text inside a German app, and
   * that is the text whose hash was stored as the record of what he agreed to.
   *
   * The column wins here rather than the browser, because the column is the one
   * she can change and the one the server obeys. Detection still decides for
   * anonymous visitors, which is correct: there is no stored preference to
   * follow before anyone has signed in.
   *
   * `resolvedLanguage` rather than `language`: a browser reporting `de-DE`
   * resolves to the `de` bundle, and comparing against the raw tag would call
   * `changeLanguage` on every render for as long as the two differ in shape.
   *
   * The import is `i18next` rather than `@/i18n`, and that is not a style
   * choice. `@/i18n` is the app BOOTSTRAP: importing it runs `.init()` with the
   * browser detector attached. The test harness initialises the same singleton
   * itself, pinned to English so component assertions are deterministic, so
   * pulling the bootstrap in here would re-init it under every test that ever
   * renders this provider. Both names are the same instance.
   */
  useEffect(() => {
    const locale = state.user?.locale;
    if (typeof locale !== 'string' || locale === '') return;
    if (i18n.resolvedLanguage === locale) return;
    void i18n.changeLanguage(locale);
  }, [state.user?.locale]);

  // Initialize session on mount: prime CSRF cookie, then probe /api/me.
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        await bootCsrf();
      } catch {
        // CSRF cookie endpoint may not yet be configured (Plan 01-02 wires Sanctum config).
        // Continue — anonymous browsing of public pages still works.
      }
      try {
        const user = await api.get<User>('/api/me');
        if (!cancelled) {
          setState({ user, isLoading: false, isInitialized: true });
        }
      } catch {
        if (!cancelled) {
          setState({ user: null, isLoading: false, isInitialized: true });
        }
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((s) => ({ ...s, isLoading: true }));
    try {
      // Magic-link: server emails a one-time link; consumption establishes the
      // session via redirect. Plan 03's LoginPage replaces this with a
      // proper "check your inbox" UX.
      await api.post('/api/auth/magic-link/request', { email: credentials.email });
      setState((s) => ({ ...s, isLoading: false }));
    } catch (err) {
      setState((s) => ({ ...s, isLoading: false }));
      throw err;
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    setState((s) => ({ ...s, isLoading: true }));
    try {
      // Same endpoint — magic-link flow auto-creates the user on first consume.
      await api.post('/api/auth/magic-link/request', { email: data.email });
      setState((s) => ({ ...s, isLoading: false }));
    } catch (err) {
      setState((s) => ({ ...s, isLoading: false }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Logout even if request fails
    }
    clearAuth();
  }, [clearAuth]);

  const refreshSession = useCallback(async () => {
    try {
      const user = await api.get<User>('/api/me');
      setState({ user, isLoading: false, isInitialized: true });
    } catch {
      clearAuth();
    }
  }, [clearAuth]);

  const value: AuthContextValue = {
    ...state,
    isAuthenticated: state.user !== null,
    login,
    register,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
